import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Instagram, Facebook, Check, Loader2, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo for coming soon page (light version for dark background)
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_paige-minerals/artifacts/adk204an_IMG_4053.png';

// Showroom background image
const SHOWROOM_BG = 'https://customer-assets.emergentagent.com/job_crystal-shop-8/artifacts/7zoiza2j_EA8D2EBD-53D6-4CC4-874F-9F408C732FF9.png';

// Navigation links (non-functional for coming soon)
const navLinks = [
    { label: 'Collection' },
    { label: 'Shop' },
    { label: 'Learn' },
    { label: 'About' },
    { label: 'Contact' },
];

const ComingSoonPage = () => {
    const [email, setEmail] = useState('');
    const [note, setNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [hoveredNav, setHoveredNav] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, note: note || null })
            });

            if (response.ok) {
                setIsSubscribed(true);
                toast.success('Thanks for subscribing! We\'ll notify you when we launch.');
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            toast.error('Unable to subscribe. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden">
            {/* Full-screen background image - positioned to show logo */}
            <div 
                className="absolute inset-0 bg-cover bg-[center_top_8%] sm:bg-[center_top_12%] bg-no-repeat"
                style={{ backgroundImage: `url(${SHOWROOM_BG})` }}
            />
            
            {/* Gradient overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/60" />

            {/* Navigation Menu - Centered at top above logo */}
            <nav className="absolute top-6 left-0 right-0 z-20 hidden md:flex items-center justify-center gap-12">
                {navLinks.map((link) => (
                    <div 
                        key={link.label}
                        className="relative"
                        onMouseEnter={() => setHoveredNav(link.label)}
                        onMouseLeave={() => setHoveredNav(null)}
                    >
                        <span className="font-body text-sm tracking-widest uppercase text-white/90 hover:text-[#D4956A] cursor-default transition-colors duration-300">
                            {link.label}
                        </span>
                        
                        {/* Coming Soon Tooltip */}
                        <AnimatePresence>
                            {hoveredNav === link.label && (
                                <motion.div
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 5 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-[#B87333] text-white text-xs rounded-md whitespace-nowrap shadow-lg"
                                >
                                    Coming Soon
                                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#B87333] rotate-45" />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </nav>

            {/* Mobile Menu Button - Top right */}
            <div className="absolute top-4 right-4 z-20 md:hidden">
                <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 bg-black/30 backdrop-blur-sm"
                    onClick={() => setMobileMenuOpen(true)}
                >
                    <Menu className="h-5 w-5" />
                </Button>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-navy"
                    >
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-12">
                                <img 
                                    src={LOGO_LIGHT}
                                    alt="Paige Minerals"
                                    className="h-16 w-auto object-contain"
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="text-white hover:bg-white/10"
                                >
                                    <X className="h-6 w-6" />
                                </Button>
                            </div>

                            <nav className="flex flex-col gap-6">
                                {navLinks.map((link, index) => (
                                    <motion.div
                                        key={link.label}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center justify-between"
                                    >
                                        <span className="font-body text-lg tracking-widest uppercase text-white/80">
                                            {link.label}
                                        </span>
                                        <span className="text-xs text-[#B87333] uppercase tracking-wider bg-[#B87333]/20 px-2 py-1 rounded">
                                            Coming Soon
                                        </span>
                                    </motion.div>
                                ))}
                            </nav>
                            
                            {/* Close hint */}
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="mt-12 text-white/40 text-sm text-center"
                            >
                                Tap X to close
                            </motion.p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Content */}
            <div className="relative z-10 min-h-screen flex flex-col items-center justify-end px-6 py-8">

                {/* Bottom Section - Content Card */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.5, delay: 0.8, ease: "easeOut" }}
                    className="w-full max-w-lg"
                >
                    {/* Glass-morphism card */}
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 sm:p-8 shadow-2xl">
                        {/* Tagline */}
                        <p className="font-display text-lg sm:text-xl text-[#D4956A] italic mb-4 text-center">
                            Collectors and curators of rare, high-quality world-wide minerals
                        </p>

                        {/* Coming Soon badge */}
                        <div className="flex justify-center mb-6">
                            <span className="inline-block px-6 py-2 bg-[#B87333]/20 border border-[#B87333]/40 rounded-full font-body text-sm text-[#D4956A] tracking-widest uppercase">
                                Coming Soon
                            </span>
                        </div>

                        {/* Email signup */}
                        {isSubscribed ? (
                            <div className="flex items-center justify-center gap-3 text-[#D4956A] py-4">
                                <div className="w-10 h-10 rounded-full bg-[#B87333]/30 flex items-center justify-center">
                                    <Check className="w-5 h-5" />
                                </div>
                                <p className="font-body text-lg">You're on the list!</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubscribe}>
                                <p className="font-body text-white/70 mb-4 text-sm text-center">
                                    Be the first to know when we launch. Join our mailing list.
                                </p>
                                <div className="flex gap-2 mb-3">
                                    <div className="relative flex-1">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Enter your email"
                                            className="pl-11 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-[#B87333] focus:ring-[#B87333]/20"
                                            required
                                            data-testid="coming-soon-email"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="bg-[#B87333] hover:bg-[#CD7F32] text-white px-6 shadow-lg"
                                        data-testid="coming-soon-subscribe"
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            'Notify Me'
                                        )}
                                    </Button>
                                </div>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Leave a note (optional)"
                                    rows={2}
                                    className="w-full px-4 py-2 bg-white/10 border border-white/30 rounded-md text-white text-sm placeholder:text-white/50 focus:border-[#B87333] focus:outline-none focus:ring-1 focus:ring-[#B87333]/20 resize-none"
                                    data-testid="coming-soon-note"
                                />
                            </form>
                        )}

                        {/* Social links */}
                        <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-white/10">
                            <a
                                href="https://instagram.com/paigeminerals"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-white/60 hover:text-[#D4956A] transition-colors group"
                                data-testid="social-instagram"
                            >
                                <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-[#B87333]/30 flex items-center justify-center transition-colors">
                                    <Instagram className="w-5 h-5" />
                                </div>
                                <span className="font-body text-sm hidden sm:inline">@paigeminerals</span>
                            </a>
                            <a
                                href="https://facebook.com/paigeminerals"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-white/60 hover:text-[#D4956A] transition-colors group"
                                data-testid="social-facebook"
                            >
                                <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-[#B87333]/30 flex items-center justify-center transition-colors">
                                    <Facebook className="w-5 h-5" />
                                </div>
                                <span className="font-body text-sm hidden sm:inline">@paigeminerals</span>
                            </a>
                        </div>
                    </div>

                    {/* Footer text */}
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                        className="mt-4 font-body text-xs text-white/40 text-center"
                    >
                        © {new Date().getFullYear()} Paige Minerals LLC. All rights reserved.
                    </motion.p>
                </motion.div>
            </div>
        </div>
    );
};

export default ComingSoonPage;
